import React, { useEffect, useState } from "react";
import { Input, FormGroup, Label } from "reactstrap";
import { showAlertAviso } from "../Parametros Horas Extras/Actividades/ActividadesHorasExtras";

interface HoraInputProps {
    label?: string;
    value?: string;
    onChange?: (hora: string) => void;
    disabled?: boolean;
    minTime?: string;
    maxTime?: string;
    errorMessage?: string;
    maxTimeErrorMessage?: string;
}

const HoraInput: React.FC<HoraInputProps> = ({ 
    label, 
    value = "", 
    onChange, 
    disabled,
    minTime,
    maxTime,
    errorMessage = "La hora debe ser mayor a la hora mínima permitida",
    maxTimeErrorMessage = "La hora debe ser menor a la hora máxima permitida"
}) => {
    const [hora, setHora] = useState(value);
    const [error, setError] = useState("");

    useEffect(() => {
        setHora(value);
    }, [value]);

    const isValidTimeFormat = (time: string): boolean => {
        return new RegExp('^([01]?[0-9]|2[0-3]):([0-5][0-9])$').test(time);
    };

    const formatTime = (time: string): string => {
        if (!time.includes(':')) return time;
        
        const [hours, minutes] = time.split(':');
        const formattedHours = hours.padStart(2, '0');
        const formattedMinutes = minutes ? minutes.padStart(2, '0') : '00';
        
        return `${formattedHours}:${formattedMinutes}`;
    };

    const validateMinTime = (time: string): boolean => {
        if (!minTime || !isValidTimeFormat(minTime)) return true;
        if (!time || !isValidTimeFormat(time)) return false;
        
        const [h, m] = time.split(':').map(Number);
        const [minH, minM] = minTime.split(':').map(Number);
        
        if (h > minH) return true;
        if (h === minH && m >= minM) return true;
        
        return false;
    };

    const validateMaxTime = (time: string): boolean => {
        if (!maxTime || !isValidTimeFormat(maxTime)) return true;
        if (!time || !isValidTimeFormat(time)) return false;
        
        const [h, m] = time.split(':').map(Number);
        const [maxH, maxM] = maxTime.split(':').map(Number);
        
        if (h < maxH) return true;
        if (h === maxH && m <= maxM) return true;
        
        return false;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;
        setError("");

        if (!new RegExp('^[0-9:]*$').test(value)) return;

        // Auto-insertar ':' después de 2 dígitos de hora
        if (!value.includes(':') && value.length === 2) {
            value = value + ':';
        }

        if (value.length > 5) return;
        if (value.includes(":")) {
            let partes = value.split(":");
            if (partes.length > 2 || partes[0].length > 2 || (partes[1] && partes[1].length > 2)) return;
        }

        // if(value.length === 4 && value.includes(':')){
        //     value = formatTime(value)
        // }

        if (value.length === 5) {
            if (minTime && isValidTimeFormat(minTime) && !validateMinTime(value)) {
                showAlertAviso(errorMessage);
                setHora("");
                if (onChange) onChange("")
                return;
            }
            
            if (maxTime && isValidTimeFormat(maxTime) && !validateMaxTime(value)) {
                showAlertAviso(maxTimeErrorMessage);
                setHora("");
                if (onChange) onChange("")
                return;
            }
            
            setError("");
        }

        setHora(value);
        if (onChange) onChange(value);
    };

    const handleBlur = (value:string) => {
        if (!hora) return;
        
        // Formatear hora incompleta (ejemplo "8:" -> "08:00")
        if (hora.includes(':') && hora.length < 5) {
            const formattedTime = formatTime(hora);

            //Se vuelve a validar si la hora cumple esto
            if (minTime && isValidTimeFormat(minTime) && !validateMinTime(formattedTime)) {
                showAlertAviso(errorMessage);
                setHora("");
                if (onChange) onChange("")
                return;
            }
            
            // //Se vuelve a validar si la hora cumple esto
            if (maxTime && isValidTimeFormat(maxTime) && !validateMaxTime(formattedTime)) {
                showAlertAviso(maxTimeErrorMessage);
                setHora("");
                if (onChange) onChange("")
                return;
            }


            setHora(formattedTime);
            if (onChange) onChange(formattedTime);
            return;
        }
        
        if (!isValidTimeFormat(hora)) {
            setHora("");
            if (onChange) onChange("");
            return;
        }
    };

    return (
        <FormGroup>
            {label && <Label for="hora">{label}</Label>}
            
            <Input
                id="hora"
                type="text"
                placeholder="HH:MM"
                value={hora}
                onChange={handleChange}
                onBlur={() => handleBlur(hora)}
                maxLength={5}
                disabled={disabled !== undefined && disabled === true}
                autoComplete={"off"}
                invalid={!!error}
            />
            
            {error && <div className="invalid-feedback" style={{display: 'block'}}>{error}</div>}
        </FormGroup>
    );
};

export default HoraInput;